import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { AuthCallback, Protected, PendingScreen } from "@/components/Protected";
import { Layout } from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Inventory from "@/pages/Inventory";
import BikeDetail from "@/pages/BikeDetail";
import BikeResolver from "@/pages/BikeResolver";
import Maintenance from "@/pages/Maintenance";
import Warehouse from "@/pages/Warehouse";
import Reports from "@/pages/Reports";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import Categories from "@/pages/Categories";
import Pricing from "@/pages/Pricing";
import Rentals from "@/pages/Rentals";
import Admin from "@/pages/Admin";
import Scanner from "@/pages/Scanner";
import PublicBooking from "@/pages/PublicBooking";
import Permalink from "@/pages/Permalink";
import Calendar from "@/pages/Calendar";
import ImportExport from "@/pages/ImportExport";
import EmailTemplates from "@/pages/EmailTemplates";
import Sedi from "@/pages/Sedi";
import Marketing from "@/pages/Marketing";
import PaymentsConfig from "@/pages/PaymentsConfig";
import Statistics from "@/pages/Statistics";
import Unsubscribe from "@/pages/Unsubscribe";

function Shell({ children }) {
  const { user } = useAuth();
  if (user && user.status !== "approved") return <PendingScreen />;
  return <Layout>{children}</Layout>;
}

function Guard({ perm, children }) {
  const { hasPerm } = useAuth();
  if (perm && !hasPerm(perm)) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/prenota" element={<PublicBooking />} />
      <Route path="/p/:token" element={<Permalink />} />
      <Route path="/disiscrizione" element={<Unsubscribe />} />
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Protected><Shell><Dashboard /></Shell></Protected>} />
      <Route path="/inventario" element={<Protected><Shell><Guard perm="inventory.view"><Inventory /></Guard></Shell></Protected>} />
      <Route path="/bici/:id" element={<Protected><Shell><Guard perm="inventory.view"><BikeDetail /></Guard></Shell></Protected>} />
      <Route path="/b/:numero" element={<Protected><Shell><BikeResolver /></Shell></Protected>} />
      <Route path="/manutenzione" element={<Protected><Shell><Guard perm="maintenance.view"><Maintenance /></Guard></Shell></Protected>} />
      <Route path="/magazzino" element={<Protected><Shell><Guard perm="warehouse.view"><Warehouse /></Guard></Shell></Protected>} />
      <Route path="/segnalazioni" element={<Protected><Shell><Guard perm="reports.view"><Reports /></Guard></Shell></Protected>} />
      <Route path="/clienti" element={<Protected><Shell><Guard perm="customers.view"><Customers /></Guard></Shell></Protected>} />
      <Route path="/clienti/:id" element={<Protected><Shell><Guard perm="customers.view"><CustomerDetail /></Guard></Shell></Protected>} />
      <Route path="/categorie" element={<Protected><Shell><Guard perm="categories.view"><Categories /></Guard></Shell></Protected>} />
      <Route path="/listini" element={<Protected><Shell><Guard perm="pricing.view"><Pricing /></Guard></Shell></Protected>} />
      <Route path="/noleggi" element={<Protected><Shell><Guard perm="rentals.view"><Rentals /></Guard></Shell></Protected>} />
      <Route path="/calendario" element={<Protected><Shell><Guard perm="rentals.view"><Calendar /></Guard></Shell></Protected>} />
      <Route path="/amministrazione" element={<Protected><Shell><Guard perm="users.manage"><Admin /></Guard></Shell></Protected>} />
      <Route path="/import-export" element={<Protected><Shell><Guard perm="import_export.view"><ImportExport /></Guard></Shell></Protected>} />
      <Route path="/template-email" element={<Protected><Shell><Guard perm="email_templates.view"><EmailTemplates /></Guard></Shell></Protected>} />
      <Route path="/marketing" element={<Protected><Shell><Guard perm="marketing.manage"><Marketing /></Guard></Shell></Protected>} />
      <Route path="/sedi" element={<Protected><Shell><Guard perm="locations.manage"><Sedi /></Guard></Shell></Protected>} />
      <Route path="/pagamenti" element={<Protected><Shell><Guard perm="payments.manage"><PaymentsConfig /></Guard></Shell></Protected>} />
      <Route path="/statistiche" element={<Protected><Shell><Guard perm="statistics.view"><Statistics /></Guard></Shell></Protected>} />
      <Route path="/scanner" element={<Protected><Shell><Scanner /></Shell></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function ThemedToaster() {
  const { resolved } = useTheme();
  return <Toaster position="top-right" theme={resolved} />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ThemeProvider>
            <AppRouter />
            <ThemedToaster />
          </ThemeProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
